<script lang="ts">
  import { Link, useLocation } from 'svelte-routing';
  import {
    ALL_MODES,
    DEFAULT_MODE,
    type FormatType,
    type GameModeConfig,
    type RegionScope,
  } from '@atlasguess/games';
  import BrandMark from './BrandMark.svelte';
  import { user } from '../lib/stores/auth';
  import { DONATIONS_ENABLED } from '../lib/features';
  import UserMenu from './UserMenu.svelte';

  interface Props {
    /** Whether the sidebar is expanded (true) or icon-only (false). Desktop. */
    expanded: boolean;
    /** Whether the mobile overlay is visible. Ignored on desktop. */
    mobileOpen: boolean;
    onToggleExpand: () => void;
    onCloseMobile: () => void;
  }

  let { expanded, mobileOpen, onToggleExpand, onCloseMobile }: Props = $props();

  const location = useLocation();

  /**
   * Hand-drawn continent silhouettes for the 6 regional country quizzes.
   * Approximate, not cartographic — just distinct enough to read at 24px
   * so each region's nav item has a recognizable mark. Drawn as a single
   * SVG path each (Oceania uses subpath `M`s for Australia + 3 island
   * dots) at the same viewBox/stroke as the other sidebar icons.
   *
   * Tuning notes: every shape leaves ~3px padding inside the 24x24
   * viewBox so the stroke doesn't clip; closed with `Z` so border
   * stroke joins clean. Tweak paths here to retune any region without
   * touching markup.
   */
  const REGION_ICON_PATHS: Partial<Record<RegionScope, string>> = {
    europe: 'M 5 7 L 9 6 L 12 8 L 15 6 L 19 7 L 19 11 L 16 13 L 14 12 L 13 16 L 10 17 L 8 14 L 5 12 Z',
    africa: 'M 8 5 L 16 5 L 18 7 L 19 11 L 17 14 L 15 18 L 12 20 L 10 16 L 6 11 L 7 7 Z',
    asia: 'M 4 6 L 12 4 L 18 5 L 21 8 L 20 12 L 18 13 L 14 13 L 13 17 L 11 19 L 9 17 L 7 13 L 4 9 Z',
    'north-america': 'M 4 5 L 12 4 L 19 6 L 20 10 L 17 13 L 17 17 L 14 16 L 12 19 L 11 21 L 9 17 L 7 12 L 5 9 Z',
    'south-america': 'M 9 4 L 15 5 L 17 9 L 16 13 L 14 18 L 12 21 L 10 19 L 8 14 L 8 7 Z',
    oceania: 'M 6 13 L 17 12 L 19 16 L 12 18 L 6 16 Z M 4 7 L 6 7 L 6 9 L 4 9 Z M 18 6 L 20 6 L 20 8 L 18 8 Z M 13 4 L 14 4 L 14 5 L 13 5 Z',
  };

  /**
   * Each mode in the registry becomes a sidebar item. `urlFor` builds the
   * canonical play URL from the scope + format slugs so a mode's id is
   * the only thing the registry needs to know about routing.
   *
   * The DEFAULT_MODE always lives at `/` (no `/play/...` mirror) — daily
   * is the landing experience, so the URL stays clean for sharing. The
   * App.svelte route table redirects the legacy `/play/{scope}/daily`
   * URL back to `/` for any external link still pointing at the old shape.
   */
  function urlFor(mode: GameModeConfig): string {
    if (mode.id === DEFAULT_MODE.id) return '/';
    const scopeSlug = `${mode.scope.entity}-${mode.scope.region}`;
    return `/play/${scopeSlug}/${mode.format}`;
  }

  function isActive(mode: GameModeConfig, currentPath: string): boolean {
    const href = urlFor(mode);
    if (currentPath === href) return true;
    // The filtered-practice URL shape `/play/:scope/:format/:filter` should
    // still highlight its parent practice item — the player is "in" that
    // mode, just looking at a sub-pool. Skip for default (href is `/`,
    // which would falsely match every nested URL via startsWith).
    if (href !== '/' && currentPath.startsWith(href + '/')) return true;
    return false;
  }

  /**
   * Group modes by format so the sidebar can show named sections.
   * Daily is rendered separately as a hero button at the top — the
   * one-puzzle-a-day entry point is too important to live inside a
   * regular labeled section. Practice + Quiz keep their section
   * headers below.
   */
  const SECTIONS: { label: string; format: FormatType }[] = [
    { label: 'Practice', format: 'practice' },
    { label: 'Quiz', format: 'quiz' },
  ];

  function getModesByFormat(format: FormatType): GameModeConfig[] {
    return ALL_MODES.filter((m) => m.format === format);
  }

  /**
   * The daily-mode config (always rendered as the hero button at the
   * top of the rail). DEFAULT_MODE is the registry's flagship daily;
   * if a future daily mode displaces it the lookup still resolves to
   * the canonical one via `format === 'daily'`.
   */
  const dailyMode = ALL_MODES.find((m) => m.format === 'daily') ?? DEFAULT_MODE;
  const dailyHref = urlFor(dailyMode);
  const dailyActive = $derived(isActive(dailyMode, $location.pathname));
</script>

<!-- Backdrop: only renders on mobile when the overlay is open. Tap to close. -->
{#if mobileOpen}
  <button
    class="sidebar-backdrop"
    aria-label="Close menu"
    onclick={onCloseMobile}
  ></button>
{/if}

<aside
  class="sidebar"
  class:sidebar--collapsed={!expanded}
  class:sidebar--mobile-open={mobileOpen}
  aria-label="Main navigation"
>
  <!-- Header: shared brand mark + floating toggle. BrandMark wraps in
       <Link to="/"> internally; the toggle stays a sibling so its click
       doesn't bubble through. The toggle is positioned absolutely within
       the header so it auto-centers vertically on the header's actual
       height. When the sidebar is collapsed the brand text fades out via
       .sidebar--collapsed .brand-mark__text. -->
  <header class="sidebar__header">
    <BrandMark />
    <button
      class="sidebar__toggle"
      aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
      onclick={onToggleExpand}
    >
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
        {#if expanded}
          <path d="M10 4 L6 8 L10 12" stroke-linecap="round" stroke-linejoin="round" />
        {:else}
          <path d="M6 4 L10 8 L6 12" stroke-linecap="round" stroke-linejoin="round" />
        {/if}
      </svg>
    </button>
  </header>

  <!-- Nav: Daily as a hero button at the top, then Leaderboards, then
       Practice + Quiz sections (loop). Empty sections are skipped so
       adding the first mode of a new format surfaces its section
       automatically. When the sidebar collapses to icon-only, section
       labels fade out and a thin divider stays to preserve the visual
       separation. -->
  <nav class="sidebar__nav">
    <!-- Hero: Daily renders as an accent-filled, full-bleed row at the
         top of the rail (no wrapper, no section header). Flush left so
         its calendar icon aligns with the brand-mark icon and the rest
         of the nav-item icon column at 18px from the rail edge. -->
    <Link
      to={dailyHref}
      getProps={() => ({
        class: 'sidebar__item sidebar__item--primary' + (dailyActive ? ' sidebar__item--active' : ''),
        'aria-current': dailyActive ? 'page' : undefined,
        title: dailyMode.displayName,
      })}
    >
      <span class="sidebar__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <rect x="4" y="5" width="16" height="15" rx="2" />
          <line x1="4" y1="10" x2="20" y2="10" />
          <line x1="9" y1="3" x2="9" y2="7" stroke-linecap="round" />
          <line x1="15" y1="3" x2="15" y2="7" stroke-linecap="round" />
        </svg>
      </span><span class="sidebar__item-label">Daily</span>
    </Link>

    <!-- Discover — the leaderboard is the primary "where do I stand"
         surface, separate from the play modes below. -->
    <div class="sidebar__group">
      <div class="sidebar__group-label" aria-hidden="true">Discover</div>
      <Link
        to="/leaderboard"
        getProps={() => ({
          class: 'sidebar__item' + ($location.pathname === '/leaderboard' ? ' sidebar__item--active' : ''),
          'aria-current': $location.pathname === '/leaderboard' ? 'page' : undefined,
          title: 'Leaderboard',
        })}
      >
        <span class="sidebar__icon" aria-hidden="true">
          <!-- Trophy: three rising bars topped with a star, all enclosed
               so the icon reads at 24px without legibility loss. -->
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 4 L18 4 L18 9 A6 6 0 0 1 6 9 Z" />
            <path d="M6 6 L3 6 L3 8 A3 3 0 0 0 6 11" />
            <path d="M18 6 L21 6 L21 8 A3 3 0 0 1 18 11" />
            <path d="M9 20 L15 20" />
            <path d="M12 14 L12 20" />
          </svg>
        </span><span class="sidebar__item-label">Leaderboards</span>
      </Link>
    </div>

    {#each SECTIONS as section (section.format)}
      {@const modesInSection = getModesByFormat(section.format)}
      {#if modesInSection.length > 0}
        <div class="sidebar__group">
          <div class="sidebar__group-label" aria-hidden="true">{section.label}</div>
          {#each modesInSection as mode (mode.id)}
            {@const href = urlFor(mode)}
            {@const active = isActive(mode, $location.pathname)}
            <Link
              to={href}
              getProps={() => ({
                class: 'sidebar__item' + (active ? ' sidebar__item--active' : ''),
                'aria-current': active ? 'page' : undefined,
                title: mode.displayName,
              })}
            >
              <span class="sidebar__icon" aria-hidden="true">
                {#if mode.format === 'daily'}
                  <!-- Calendar icon: one-per-day signal outranks the entity glyph -->
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="4" y="5" width="16" height="15" rx="2" />
                    <line x1="4" y1="10" x2="20" y2="10" />
                    <line x1="9" y1="3" x2="9" y2="7" stroke-linecap="round" />
                    <line x1="15" y1="3" x2="15" y2="7" stroke-linecap="round" />
                  </svg>
                {:else if mode.scope.entity === 'cities'}
                  <!-- City icon: filled dot inside a ring -->
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="3" fill="currentColor" />
                  </svg>
                {:else if REGION_ICON_PATHS[mode.scope.region]}
                  <!-- Region-specific continent silhouette (quiz modes). -->
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path
                      d={REGION_ICON_PATHS[mode.scope.region]}
                      stroke-linejoin="round"
                      stroke-linecap="round"
                    />
                  </svg>
                {:else}
                  <!-- Default country/region icon: rounded polygon -->
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M5 7 L12 4 L19 7 L18 17 L11 20 L4 17 Z" stroke-linejoin="round" />
                  </svg>
                {/if}
              </span><span class="sidebar__item-label">{mode.shortName ?? mode.displayName}</span>
            </Link>
          {/each}
        </div>
      {/if}
    {/each}
  </nav>

  <!-- Mobile-only legal-links strip. Lives here (not the AppFooter)
       because the bottom-right AppFooter overlaps game UI on mobile.
       Hidden on desktop where the AppFooter handles it. -->
  <div class="sidebar__legal" aria-label="Legal">
    <Link to="/privacy" getProps={() => ({ class: 'sidebar__legal-link' })}>
      Privacy
    </Link>
    <span class="sidebar__legal-sep" aria-hidden="true">&middot;</span>
    <Link to="/terms" getProps={() => ({ class: 'sidebar__legal-link' })}>
      Terms
    </Link>
    <span class="sidebar__legal-sep" aria-hidden="true">&middot;</span>
    <Link to="/contact" getProps={() => ({ class: 'sidebar__legal-link' })}>
      Contact
    </Link>
    <span class="sidebar__legal-sep" aria-hidden="true">&middot;</span>
    {#if DONATIONS_ENABLED}
      <Link to="/donate" getProps={() => ({ class: 'sidebar__legal-link' })}>
        Donate
      </Link>
      <span class="sidebar__legal-sep" aria-hidden="true">&middot;</span>
    {/if}
    <!-- /discord is a Cloudflare Pages 302 — see _redirects + AppFooter
         comment for why the href stays on our domain. -->
    <a
      href="/discord"
      target="_blank"
      rel="noopener noreferrer"
      class="sidebar__legal-link"
    >
      Discord
    </a>
  </div>

  <!-- Footer: account row only. The account row mirrors the TopNav's
       auth state — logged-out shows Sign in (linking to /login),
       logged-in shows the shared UserMenu dropdown. UserMenu opens
       upward in this shell because there's no room beneath the rail. -->
  <footer class="sidebar__footer">
    {#if $user}
      <UserMenu user={$user} placement="top-stretch" triggerVariant="wide" />
    {:else}
      <Link
        to="/login"
        getProps={() => ({
          class: 'sidebar__user-link',
          title: 'Sign in',
          'aria-label': 'Sign in',
        })}
      >
        <span class="sidebar__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="9" r="3.5" />
            <path d="M5 20 C5 16 8 14 12 14 S19 16 19 20" />
          </svg>
        </span><span class="sidebar__user-text">Sign in</span>
      </Link>
    {/if}
  </footer>
</aside>
